import { Component, inject, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatDialogRef } from "@angular/material/dialog";

import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { DropdownSelectMapOption } from "@bitwarden/web-vault/app/directives/dropdown.directive";
import { PaymentType } from "@bitwarden/web-vault/app/services/notification/push-notification.service";

type NotifyDays = 1 | 3 | 7;

@Component({
  selector: "app-bill-alert-dialog",
  templateUrl: "bill-alert-dialog.component.html",
})
export class BillAlertDialogComponent implements OnInit {
  private i18nService = inject(I18nService);
  private fb = inject(FormBuilder);
  protected readonly dialogRef = inject<MatDialogRef<BillAlertDialogComponent>>(MatDialogRef);

  protected form: FormGroup;
  protected selectedPaymentType: string = "";
  protected selectedDueDate: string = "";
  protected selectedNotifyDays: NotifyDays[] = [];
  protected isTouched = false;

  protected readonly notifyOptions: { days: NotifyDays; label: string }[] = [
    { days: 1, label: this.i18nService.t("notifyOneDay") },
    { days: 3, label: this.i18nService.t("notifyThreeDays") },
    { days: 7, label: this.i18nService.t("notifyOneWeek") },
  ];

  protected readonly paymentTypeOptions: DropdownSelectMapOption[] = [
    {
      realValue: "bill",
      optionDisplayValue: this.i18nService.t("paymentTypeBill"),
      selectedDisplayValue: this.i18nService.t("paymentTypeBill"),
    },
    {
      realValue: "subscription",
      optionDisplayValue: this.i18nService.t("paymentTypeSubscription"),
      selectedDisplayValue: this.i18nService.t("paymentTypeSubscription"),
    },
    {
      realValue: "loan",
      optionDisplayValue: this.i18nService.t("paymentTypeLoan"),
      selectedDisplayValue: this.i18nService.t("paymentTypeLoan"),
    },
    {
      realValue: "insurance",
      optionDisplayValue: this.i18nService.t("paymentTypeInsurance"),
      selectedDisplayValue: this.i18nService.t("paymentTypeInsurance"),
    },
    {
      realValue: "utilities",
      optionDisplayValue: this.i18nService.t("paymentTypeUtilities"),
      selectedDisplayValue: this.i18nService.t("paymentTypeUtilities"),
    },
    {
      realValue: "rent",
      optionDisplayValue: this.i18nService.t("paymentTypeRent"),
      selectedDisplayValue: this.i18nService.t("paymentTypeRent"),
    },
    {
      realValue: "creditCard",
      optionDisplayValue: this.i18nService.t("paymentTypeCreditCard"),
      selectedDisplayValue: this.i18nService.t("paymentTypeCreditCard"),
    },
    {
      realValue: "other",
      optionDisplayValue: this.i18nService.t("paymentTypeOther"),
      selectedDisplayValue: this.i18nService.t("paymentTypeOther"),
    },
  ];

  ngOnInit() {
    this.form = this.fb.group({
      payee: ["", [Validators.required, Validators.maxLength(100)]],
      amount: [null, [Validators.min(0.01)]],
    });
  }

  protected onPaymentTypeSelected(value: string) {
    this.selectedPaymentType = value;
  }

  protected onDueDateChanged(date: string) {
    this.selectedDueDate = date;
  }

  protected selectNotifyDays(days: NotifyDays) {
    const index = this.selectedNotifyDays.indexOf(days);
    if (index >= 0) {
      this.selectedNotifyDays = this.selectedNotifyDays.filter((d) => d !== days);
    } else {
      this.selectedNotifyDays = [...this.selectedNotifyDays, days];
    }
  }

  protected isNotifyDaySelected(days: NotifyDays): boolean {
    return this.selectedNotifyDays.includes(days);
  }

  protected isFormValid(): boolean {
    return (
      this.form.valid &&
      !!this.selectedPaymentType &&
      !!this.selectedDueDate &&
      this.selectedNotifyDays.length > 0
    );
  }

  protected save() {
    this.isTouched = true;
    if (!this.isFormValid()) {
      this.form.markAllAsTouched();
      return;
    }

    const result = {
      paymentType: this.selectedPaymentType as PaymentType,
      payee: this.form.value.payee,
      dueDate: this.selectedDueDate,
      amount: this.form.value.amount != null ? Number(this.form.value.amount) : undefined,
      notifyDaysBefore: [...this.selectedNotifyDays],
    };

    this.dialogRef.close(result);
  }

  protected close() {
    this.dialogRef.close(null);
  }
}
